import { useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

const routeLabels: Record<string, string> = {
  '/products': 'Products',
  '/categories': 'Categories',
  '/sellers': 'Sellers',
  '/contact': 'Contact',
  '/help': 'Help Center',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
  '/refund-policy': 'Refund Policy',
  '/returns': 'Returns',
  '/shipping-info': 'Shipping Info',
  '/track-order': 'Track Order',
  '/checkout': 'Checkout',
  '/payment': 'Payment',
  '/payment-success': 'Payment Success',
  '/auth': 'Sign In',
  '/role-selection': 'Choose Role',
  '/seller/dashboard': 'Dashboard',
  '/seller/products': 'Products',
  '/seller/add-product': 'Add Product',
  '/seller/orders': 'Orders',
  '/seller/catalog': 'Catalog',
  '/seller/bank-accounts': 'Bank Accounts',
  '/seller/whatsapp-settings': 'WhatsApp Settings',
  '/buyer/orders': 'My Orders',
  '/buyer/bank-accounts': 'Bank Accounts',
  '/admin/dashboard': 'Admin Dashboard',
};

// Static pages that should show back button  
const staticPages = [
  '/privacy', '/terms', '/refund-policy', '/returns', 
  '/shipping-info', '/contact', '/help', '/auth',
  '/role-selection', '/404'
];

export function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Don't show breadcrumbs on homepage
  if (location.pathname === '/') {
    return null;
  }

  // Check if current page should show back button instead of breadcrumbs
  const showBackButton = staticPages.some(page => location.pathname.startsWith(page)) || 
                        location.pathname === '/404' ||
                        location.pathname.includes('/404');

  if (showBackButton) {
    return (
      <div className="mb-6">
        <button 
          onClick={() => window.history.back()}
          className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronRight className="h-4 w-4 mr-1 rotate-180" />
          Back
        </button>
      </div>
    );
  }

  // Handle dynamic routes like /products/:id or /share/:id
  const generateBreadcrumbs = () => {
    const crumbs = [{ label: 'Home', path: '/' }];
    let currentPath = '';

    pathnames.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Check if this is a UUID (product ID, etc.)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
      
      if (isUUID) {
        // For product pages, show "Product Details"
        if (pathnames[index - 1] === 'products') {
          crumbs.push({ label: 'Product Details', path: currentPath });
        } else if (pathnames[index - 1] === 'share') {
          crumbs.push({ label: 'Product', path: currentPath });
        } else if (pathnames[index - 1] === 'seller' && pathnames[index + 1] === 'edit') {
          crumbs.push({ label: 'Edit Product', path: currentPath });
        } else {
          crumbs.push({ label: 'Details', path: currentPath });
        }
      } else {
        const label = routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1);
        crumbs.push({ label, path: currentPath });
      }
    });

    return crumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground mb-6">
      {breadcrumbs.map((crumb, index) => (
        <div key={crumb.path} className="flex items-center">
          {index > 0 && <ChevronRight className="h-4 w-4 mx-2" />}
          {index === 0 && <Home className="h-4 w-4 mr-1" />}
          {index === breadcrumbs.length - 1 ? (
            <span className="text-foreground font-medium">{crumb.label}</span>
          ) : (
            <button 
              onClick={() => window.history.back()}
              className="hover:text-foreground transition-colors"
            >
              {crumb.label}
            </button>
          )}
        </div>
      ))}
    </nav>
  );
}